import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      logger?.debug('AUTH', 'Loading user profile', { userId })
      try {
        logger?.apiCall('user_profiles', 'SELECT', { userId })
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        if (error) {
          logger?.error('AUTH', 'Profile load failed', { error: error?.message, userId })
        } else {
          logger?.info('AUTH', 'Profile loaded successfully', { 
            userId, 
            role: data?.role,
            hasProfile: !!data 
          })
          setUserProfile(data)
        }
      } catch (error) {
        logger?.networkError('Profile load', error)
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      logger?.debug('AUTH', 'Clearing user profile')
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      logger?.info('AUTH', 'Auth state change detected', { 
        event, 
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email
      })
      
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        logger?.debug('AUTH', 'Session active, loading profile', { userId: session?.user?.id })
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        logger?.debug('AUTH', 'No session, clearing profile')
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    logger?.info('AUTH', 'AuthProvider mounted, checking initial session')
    
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      logger?.debug('AUTH', 'Initial session check complete', { 
        hasSession: !!session,
        userId: session?.user?.id 
      })
      authStateHandlers?.onChange(null, session)
    })?.catch((error) => {
      logger?.networkError('Initial session check', error)
    })

    // CRITICAL: This must remain synchronous
    logger?.debug('AUTH', 'Setting up auth state listener')
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      logger?.debug('AUTH', 'Unsubscribing from auth state changes')
      subscription?.unsubscribe()
    };
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    logger?.authAttempt(email)
    logger?.authRequest('signInWithPassword', { email })
    
    try {
      const startTime = performance.now()
      logger?.debug('AUTH', 'Calling Supabase signInWithPassword', { email })
      
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      const duration = performance.now() - startTime
      logger?.debug('AUTH', `signInWithPassword completed in ${duration?.toFixed(2)}ms`)
      
      if (error) {
        logger?.authError('signInWithPassword', error)
        logger?.error('AUTH', 'Sign in failed', {
          email,
          errorMessage: error?.message,
          errorStatus: error?.status,
          errorCode: error?.code
        })
      } else {
        logger?.authResponse('signInWithPassword', true, data)
        logger?.info('AUTH', 'Sign in successful', {
          userId: data?.user?.id,
          email: data?.user?.email,
          role: data?.user?.user_metadata?.role
        })
      }
      
      return { data, error }
    } catch (error) {
      logger?.networkError('signIn', error)
      logger?.error('AUTH', 'Sign in network error', {
        email,
        errorName: error?.name,
        errorMessage: error?.message,
        isTypeError: error instanceof TypeError
      })
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    logger?.info('AUTH', 'Sign out initiated')
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        logger?.info('AUTH', 'Sign out successful')
        setUser(null)
        profileOperations?.clear()
      } else {
        logger?.authError('signOut', error)
      }
      return { error }
    } catch (error) {
      logger?.networkError('signOut', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) {
      logger?.warn('AUTH', 'Update profile called without user')
      return { error: { message: 'No user logged in' } }
    }
    
    logger?.debug('AUTH', 'Updating user profile', { userId: user?.id, updates })
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) {
        logger?.info('AUTH', 'Profile updated successfully', { userId: user?.id })
        setUserProfile(data)
      } else {
        logger?.error('AUTH', 'Profile update failed', { error: error?.message })
      }
      return { data, error }
    } catch (error) {
      logger?.networkError('updateProfile', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    userRole: userProfile?.role || 'student',
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
